import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken, Provider } from '@angular/core';
import { LaravelResult } from '@fsf/util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  NewSavedSimulation,
  SavedSimulation,
  savedSimulationSchema,
} from '../models/saved-simulation.model';

/**
 * Behind a token so the `@fsf/discover` lib takes no dependency on the app's `environment`,
 * and the root `LaravelApi` (bound to `/api/candidate-sites`) stays untouched.
 */
export const SAVED_SIMULATION_ENDPOINT = new InjectionToken<string>(
  'saved-simulation.endpoint.url',
);

export interface SavedSimulationApiConfig {
  http: HttpClient;
  endpointUrl: string;
}

/**
 * Responses are Zod-parsed at the boundary. `readMany` deliberately parses **per element** and
 * drops a malformed row rather than failing whole: the dropdown is shared across the company,
 * so one tampered blob must not break it for every member.
 *
 * Not `@Injectable` — only ever built via `new` in `provideSavedSimulationApi`, so AOT must not
 * analyze its config-object constructor parameter as a DI token.
 */
export class SavedSimulationApiRepository {
  readonly #http: HttpClient;
  readonly #endpointUrl: string;

  constructor(config: SavedSimulationApiConfig) {
    this.#http = config.http;
    this.#endpointUrl = config.endpointUrl;
  }

  /** Malformed rows are dropped with a warning rather than failing the whole list. */
  readMany(siteId: string): Observable<SavedSimulation[]> {
    return this.#http
      .get<LaravelResult<unknown[]>>(this.#endpointUrl, {
        params: { site_id: siteId },
      })
      .pipe(
        map((result) => {
          const rows = result.data ?? [];
          const valid: SavedSimulation[] = [];
          for (const row of rows) {
            const parsed = savedSimulationSchema.safeParse(row);
            if (parsed.success) {
              valid.push(parsed.data);
            } else {
              // Drop the offending row only; the rest of the shared pool still loads.
              console.warn(
                '[SavedSimulationApiRepository] dropping malformed saved-simulation row',
                parsed.error,
              );
            }
          }
          return valid;
        }),
      );
  }

  /** Parsed strictly, unlike `readMany`: a malformed create errors instead of emitting. */
  addOne(input: NewSavedSimulation): Observable<SavedSimulation> {
    return this.#http
      .post<LaravelResult<unknown>>(this.#endpointUrl, {
        name: input.name,
        parameters: input.parameters,
        siteId: input.siteId,
      })
      .pipe(map((result) => savedSimulationSchema.parse(result.data)));
  }

  /** Hard delete. */
  removeOne(id: string): Observable<void> {
    return this.#http
      .delete<void>(`${this.#endpointUrl}/${id}`)
      .pipe(map(() => undefined));
  }
}

/**
 * `HttpClient` and the URL are resolved with `inject` inside the factory, which runs in an
 * injection context, and handed to the config constructor.
 */
export function provideSavedSimulationApi(): Provider {
  return {
    provide: SavedSimulationApiRepository,
    useFactory: () =>
      new SavedSimulationApiRepository({
        http: inject(HttpClient),
        endpointUrl: inject(SAVED_SIMULATION_ENDPOINT),
      }),
  };
}
